import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, AlertCircle } from 'lucide-react';
import type { DocumentItem } from '../../types/documents';

interface DocumentUploaderProps {
  documentItem: DocumentItem;
  onUpload: (file: File) => Promise<void>;
  isUploading?: boolean;
  disabled?: boolean;
}

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  documentItem,
  onUpload,
  isUploading = false,
  disabled = false
}) => {
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !documentItem.extensions.includes(fileExt)) {
      return `Format non supporté. Formats acceptés: ${documentItem.extensions.join(', ')}`;
    }
    
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > documentItem.maxMB) {
      return `Fichier trop volumineux. Taille maximum: ${documentItem.maxMB}MB`;
    }
    
    return null;
  };

  const handleUpload = async (file: File) => {
    setError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await onUpload(file);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du téléversement');
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleUpload(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: documentItem.extensions.reduce((acc, ext) => {
      const mimeTypes = {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg'
      };
      const mimeType = mimeTypes[ext as keyof typeof mimeTypes];
      if (mimeType) {
        acc[mimeType] = [`.${ext}`];
      }
      return acc;
    }, {} as Record<string, string[]>),
    maxFiles: 1,
    disabled: disabled || isUploading
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${(disabled || isUploading) ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        {isUploading ? (
          <div className="flex flex-col items-center space-y-2">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-blue-600 font-medium text-sm">Téléversement en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <Upload className="w-6 h-6 text-gray-400" />
            <div>
              <p className="text-gray-600 text-sm">
                {isDragActive 
                  ? 'Déposez le fichier ici...' 
                  : 'Glissez-déposez un fichier ou cliquez pour sélectionner'
                }
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {documentItem.extensions.join(', ')} • Max {documentItem.maxMB}MB
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}
    </div>
  );
};